import React from "react";
import { Typography, Box, Tabs, Tab, Paper } from "@material-ui/core";
import AgentImporter from "./AgentImporter";
import LeaderImporter from "./LeaderImporter";

/**
 * Component that renders a page for importing commission data for agents and
 * leaders. The component uses a tabbed interface to allow the user to select
 * between importing agent commissions and importing leader commissions.
 *
 * The component renders a tabpanel for each type of import, with a heading
 * and a paragraph of explanatory text. The component also renders a tab
 * bar with two tabs: one for importing agent commissions and one for importing
 * leader commissions. The component uses a state variable `activeTab` to
 * keep track of which tab is currently selected.
 *
 * The component also defines a nested function `TabPanel` that is used to
 * render the content of each tab based on the `activeTab` state variable.
 */
export default function ImportTab() {
  const [activeTab, setActiveTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <div>
      <Typography variant="h5" gutterBottom>
        Import Commission Data
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Import agent and leader commission data for processing.
      </Typography>

      <Paper elevation={1}>
        <Box p={2}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="Import tabs"
          >
            <Tab label="Agent Commissions" id="import-tab-0" />
            <Tab label="Leader Commissions" id="import-tab-1" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <AgentImporter />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <LeaderImporter />
          </TabPanel>
        </Box>
      </Paper>
    </div>
  );
}

/**
 * A component that renders a tab panel.
 *
 * This component renders a `div` element with the `role` attribute set to
 * `"tabpanel"`, and the `hidden` attribute set to `true` if the `value` prop
 * does not match the `index` prop. The component also renders a nested `Box`
 * element, which is used to set the padding of the tab panel content. The
 * component also renders a nested `Typography` element, which is used to
 * display the tab panel content.
 *
 * The component accepts the following props:
 *
 * - `children`: The content of the tab panel.
 * - `value`: The currently active tab index.
 * - `index`: The index of this tab panel.
 * - `other`: Any additional props that should be passed to the `div` element.
 *
 * The component uses the `id` and `aria-labelledby` attributes to associate
 * the tab panel with its corresponding tab in the tab bar.
 *
 * @param {{children: React.ReactNode, value: number, index: number, [key: string]: any}} props
 * The props for the component.
 * @returns {React.ReactElement}
 * The rendered component.
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`import-tabpanel-${index}`}
      aria-labelledby={`import-tab-${index}`}
      {...other}
    >
      {/* Only render the content if the tab is active */}
      {value === index && <Box pt={3}>{children}</Box>}
    </div>
  );
}
